import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LogErrors } from './../../../../../utilities/LogErrors'

//import { InstructorBase } from './../../../../Layouts/Instructor/InstructorBase'
import { InstructorBase } from './../../../../Layouts/Instructor/V2/InstructorBase'
import { Search } from './../../../../Common/Tables/Search'
import { ValidateSession } from './../../../../../utilities/BL/Sessions'
import { useFloatMessage, MESSAGE_TYPE } from './../../../../Providers/FloatMessage'

import { GetAllResults as BLGetAllResults, GetFilteredResults as BLGetFilteredResults, GetReportDetails as BLGetReportDetails } from './../../../../../utilities/BL/Instructor/Results'
import { BuildAndDownloadWorkbook, JsonToSheet, FormatDateTime, BuildSheetWithMeta } from './../../../../../utilities/BL/Instructor/ExcelExporter'
import { GetCourses as BLGetCourses, GetEvaluations as BLGetEvaluations } from './../../../../../utilities/BL/Instructor/Agendas'
import { GetAllDataGroups as BLGetAllDataGroups } from './../../../../../utilities/BL/Instructor/Groups'
import { Loading } from './../../../../Common/Loading'
import { HELP_TYPE } from './../../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../../utilities/EL/WebPages'

//import { HELP_TYPE } from './../../../../Header/Header'
//import { useTawkChat } from './../../../../../Support/Chat/Chat'



const Results = () => {
  const navigate = useNavigate()
  const { showMessage } = useFloatMessage()

  const [isLoading, setIsLoading] = useState(true)

  // --- Paso a paso (curso → grupo → evaluación) ---
  const [courses, setCourses] = useState(null)
  const [selectedCourseId, setSelectedCourseId] = useState(0)
  const [isCoursesListLoading, setIsCoursesListLoading] = useState(false)

  const [groups, setGroups] = useState(null)
  const [selectedGroupId, setSelectedGroupId] = useState(0)
  const [isGroupsListLoading, setIsGroupsListLoading] = useState(false)

  const [evaluations, setEvaluations] = useState(null)
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(0)
  const [isEvaluationsListLoading, setIsEvaluationsListLoading] = useState(false)

  // --- Tabla y búsqueda (DESGLOSADA) ---
  const [allRows, setAllRows] = useState([])      // filas crudas de la API (página actual)
  const [searchText, setSearchText] = useState('')
  const [filteredRows, setFilteredRows] = useState([])

  const allRowsRef = useRef([])
  useEffect(() => { allRowsRef.current = allRows }, [allRows])

  useEffect(() => {
    const text = (searchText ?? '').toLowerCase()
    if (!text) { setFilteredRows(allRows); return }

    const filtered = allRows.filter(r => {
      const fields = [
        `${r?.name ?? ''} ${r?.lastName ?? ''}`,
        r?.user,
        r?.attemptNumber,
        r?.blockName ?? r?.nameEvaluation ?? r?.type,
        r?.finishDate ? FormatDateTime(r.finishDate) : '',
        r?.score
      ]
      return fields.some(v => String(v ?? '').toLowerCase().includes(text))
    })
    setFilteredRows(filtered)
  }, [searchText, allRows])

  // --- Control de carga/UI ---
  const [isWorking, setIsWorking] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  // --- Paginación (del backend) ---
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [hasMore, setHasMore] = useState(false)

  // --- Info de centro para reportes (opcional si luego lo usas) ---
  const [centerInfo, setCenterInfo] = useState({ idCenter: null, centerName: '' })

  // ===== Helpers =====
  const normalizeCourses = (arr) => (arr ?? []).map(c => ({ id: c.idCourse, name: c.nameCourse }))
  const normalizeGroups = (resp) => {
    if (!resp?.success) return []
    return [
      ...(resp?.data?.visibles ?? []).map(x => ({ ...x, visible: true })),
      ...(resp?.data?.noVisibles ?? []).map(x => ({ ...x, visible: false })),
    ]
  }

  const getEvaluationTypeName = (evaluationType, fallbackBlockName) => {
    switch (Number(evaluationType)) {
      case 1: return 'Diagnóstico'
      case 2: return fallbackBlockName || 'Parcial'
      case 3: return 'General'
      case 4: return 'Acreditación'
      case 5: return 'Certificación'
      default: return fallbackBlockName || 'Evaluación'
    }
  }

  // ---------- Helpers de Excel (OBJETOS + try/catch) ----------
  const buildMetaRows = ({ header, metadata }) => {
    const { studentName, groupName, courseName } = header ?? {}
    const {
      evaluationType, finishDate, attemptNumber,
      totalQuestions, correctQuestions, incorrectQuestions, score, blockName
    } = metadata ?? {}

    return [
      ['Nombre:',                      studentName ?? '-'],
      ['Grupo:',                       groupName ?? '-'],
      ['Curso:',                       courseName ?? '-'],
      ['Evaluación:',                  getEvaluationTypeName(evaluationType, blockName)],
      ['Fecha en que se presentó la evaluación:', finishDate ? FormatDateTime(finishDate) : '-'],
      ['Número de oportunidad:',       attemptNumber ?? '-'],
      ['Total de preguntas:',          totalQuestions ?? '-'],
      ['Preguntas correctas:',         correctQuestions ?? '-'],
      ['Preguntas incorrectas:',       incorrectQuestions ?? '-'],
      ['Calificación:',                score ?? '-'],
      [],
      ['Detalle de evaluación:', '']
    ]
  }

  // Construye filas-objeto para la tabla (siempre %)
  const buildDetailObjectRows = ({ evaluationType, details, finalAveragePercent }) => {
    const isPartial = Number(evaluationType) === 2

    // Encabezados de la tabla
    const headerOrder  = ['blockName', 'itemName', 'result']
    const headerLabels = isPartial
      ? { blockName: 'Nombre del bloque', itemName: 'Tema', result: 'Aprobado/No aprobado' }
      : { blockName: 'Nombre del bloque', itemName: 'Tema', result: 'Resultado' }

    // Filas como OBJETOS
    const safeDetails = Array.isArray(details) ? details : []
    const rows = safeDetails.map(d => ({
      blockName: d?.blockName ?? '-',
      itemName:  d?.itemName  ?? '-',
      result:    `${d?.percent ?? 0}%`
    }))

    // Fila "Promedio"
    rows.push({ blockName: '', itemName: '', result: '' })
    rows.push({ blockName: '', itemName: '', result: '' })
    rows.push({ blockName: 'Promedio', itemName: '', result: `${finalAveragePercent ?? 0}%` })

    return { headerOrder, headerLabels, rows }
  }

  const buildWorksheetForReport = ({ header, metadata, details, finalAveragePercent }) => {
    try {
      if (!metadata) throw new Error('metadata vacío')
      if (metadata?.evaluationType == null) throw new Error('evaluationType ausente en metadata')
      if (!Array.isArray(details)) {
        // No detenemos: generamos tabla vacía con aviso
        details = []
      }

      const metaRows = buildMetaRows({ header, metadata })
      const { headerOrder, headerLabels, rows } = buildDetailObjectRows({
        evaluationType: metadata.evaluationType,
        details,
        finalAveragePercent
      })

      const colWidths = [32, 64, 16]
      const ws = BuildSheetWithMeta({
        metaRows,
        rows,                // OBJETOS
        headerOrder,         // KEYS
        headerLabels,        // Encabezados amigables
        colWidths
      })
      return ws
    } catch (err) {
      // Fallback: hoja mínima para no dejar al usuario sin archivo
      const msg = `No fue posible construir el detalle del reporte (${err?.message ?? 'error desconocido'}).`
      console.error('buildWorksheetForReport:', err)
      // Hoja simple con el mensaje
      const metaRows = [
        ['Reporte', ''],
        ['Estado', msg],
      ]
      const ws = BuildSheetWithMeta({
        metaRows,
        rows: [{ note: 'Sin datos de detalle' }],
        headerOrder: ['note'],
        headerLabels: { note: 'Nota' },
        colWidths: [32]
      })
      return ws
    }
  }
  // ------------------------------------------------------------

  // ===== Cargas iniciales =====
  const LoadPage = async () => {
    try {
      setIsWorking(true)
      setErrorMessage(null)
      setIsCoursesListLoading(true)

      const response = await BLGetCourses()
      if (response.success) {
        setCourses(normalizeCourses(response.data))
      } else {
        setErrorMessage(response?.message ?? 'No fue posible cargar cursos.')
      }
    } catch (error) {
      await LogErrors('LoadPage - Results', error)
      setErrorMessage('Ocurrió un error al cargar cursos.')
    } finally {
      setIsWorking(false)
      setIsLoading(false)
      setIsCoursesListLoading(false)
    }
  }

  useEffect(() => {
    async function Validate() {
      const tmpSession = await ValidateSession()
      if (tmpSession?.success) {
        if (tmpSession.data?.user?.userStatus === 2) {
          const user = tmpSession.data?.user
          const idCenter = user?.idCenter ?? user?.centerId ?? user?.center?.id ?? null
          const centerName = user?.centerName ?? user?.center?.name ?? ''
          setCenterInfo({ idCenter, centerName })
          LoadPage()
        } else {
          navigate('/confirm')
        }
      } else {
        navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
      }
    }
    Validate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ===== Handlers de selección encadenada =====
  const handleSelectCourse = async (courseId) => {
    try {
      setIsGroupsListLoading(true)
      setSelectedCourseId(courseId)
      setSelectedGroupId(0)
      setSelectedEvaluationId(0)
      setGroups(null)
      setEvaluations(null)
      setAllRows([])
      setFilteredRows([])
      setSearchText('')
      setPageNumber(1)
      setHasMore(false)

      if (!courseId) return

      setIsWorking(true)
      setErrorMessage(null)

      const response = await BLGetAllDataGroups()
      if (response.success) {
        setGroups(normalizeGroups(response))
      } else {
        setErrorMessage(response?.message ?? 'No fue posible cargar grupos.')
      }
    } catch (e) {
      await LogErrors('handleSelectCourse - Results', e)
      setErrorMessage('Ocurrió un error al cargar grupos.')
    } finally {
      setIsWorking(false)
      setIsGroupsListLoading(false)
    }
  }

  const handleSelectGroup = async (groupId) => {
    try {
      setIsEvaluationsListLoading(true)
      setSelectedGroupId(groupId)
      setSelectedEvaluationId(0)
      setEvaluations(null)
      setAllRows([])
      setFilteredRows([])
      setSearchText('')
      setPageNumber(1)
      setHasMore(false)

      if (!groupId || !selectedCourseId) return

      setIsWorking(true)
      setErrorMessage(null)

      const response = await BLGetEvaluations(selectedCourseId)
      if (response.success) {
        setEvaluations(response.data ?? [])
      } else {
        setErrorMessage(response?.message ?? 'No fue posible cargar evaluaciones.')
      }
    } catch (e) {
      await LogErrors('handleSelectGroup - Results', e)
      setErrorMessage('Ocurrió un error al cargar evaluaciones.')
    } finally {
      setIsWorking(false)
      setIsEvaluationsListLoading(false)
    }
  }

  const handleSelectEvaluation = (evaluationId) => {
    try {
      setIsLoading(true)
      setSelectedEvaluationId(evaluationId)
      setAllRows([])
      setFilteredRows([])
      setSearchText('')
      setPageNumber(1)
      setHasMore(false)
    } catch {/* no-op */}
    finally { setIsLoading(false) }
  }

  // ===== Aplicar Filtro (DESGLOSADO + PAGINACIÓN) =====
  const GetFilteredResults = async (page = pageNumber, size = pageSize) => {
    try {
      setIsLoading(true)

      if (!selectedCourseId || !selectedGroupId || !selectedEvaluationId) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: 'Completa los 3 pasos para aplicar el filtro.' })
        return
      }

      showMessage({ type: MESSAGE_TYPE.INFO, text: 'Obteniendo resultados solicitados...' })
      setIsWorking(true)
      setErrorMessage(null)

      const response = await BLGetFilteredResults({
        courseId: selectedCourseId,
        groupId: selectedGroupId,
        evaluationId: selectedEvaluationId,
        pageNumber: page,
        pageSize: size
      })

      if (response.success) {
        const payload = response.data ?? {}
        const rows = Array.isArray(payload) ? payload : (payload.rows ?? [])
        setAllRows(rows)
        setFilteredRows(rows)

        if (!Array.isArray(payload)) {
          setPageNumber(payload.page ?? page)
          setPageSize(payload.pageSize ?? size)
          setHasMore(Boolean(payload.hasMore))
        }

        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mostrando resultados' })
      } else {
        setAllRows([])
        setFilteredRows([])
        setHasMore(false)
        showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message ?? 'No ha sido posible obtener resultados.' })
      }
    } catch (error) {
      await LogErrors('GetFilteredResults - Results', error)
      showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha ocurrido un error al obtener los resultados.' })
    } finally {
      setIsWorking(false)
      setIsLoading(false)
    }
  }

  const gotoPrevPage = () => {
    const prev = Math.max(1, pageNumber - 1)
    if (prev !== pageNumber) GetFilteredResults(prev, pageSize)
  }
  const gotoNextPage = () => {
    if (hasMore) GetFilteredResults(pageNumber + 1, pageSize)
  }

  // ===== Exportar reporte por fila =====
  const ExportSingleReport = async (row) => {
    try {
      setIsLoading(true)
      showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando informe en Excel...' })

      // nombres visibles por si faltan en row
      const groupNameFromState = (groups ?? []).find(g => g.idGroup === (row.idGroup ?? selectedGroupId))?.groupName ?? row.groupName ?? ''
      const courseNameFromState = (courses ?? []).find(c => c.id === (row.idCourse ?? selectedCourseId))?.name ?? row.courseName ?? ''

      // Payload coherente con tu BL actual
      const payload = {
        studentId: row.idZtudiante ?? row.studentId ?? row.id ?? null,
        courseId: row.idCourse ?? selectedCourseId ?? null,
        evaluationToken: row.token,
        groupId: row.idGroup ?? selectedGroupId ?? null,
        groupName: groupNameFromState,
        attemptNumber: row.attemptNumber ?? 1
      }

      const resp = await BLGetReportDetails(payload)
      if (!resp?.success) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: resp?.message ?? 'No fue posible obtener el detalle del reporte.' })
        return
      }

      const data = resp.data ?? {}
      const { header, metadata, details, finalAveragePercent } = data

      const enrichedHeader = {
        ...header,
        groupName: header?.groupName || groupNameFromState,
        courseName: header?.courseName || courseNameFromState
      }

      const ws = buildWorksheetForReport({
        header: enrichedHeader,
        metadata,
        details,
        finalAveragePercent
      })

      const studentForFile = (enrichedHeader?.studentName || 'Alumno').replace(/[\\/:*?"<>|]/g,'_')
      const when = metadata?.finishDate ? FormatDateTime(metadata.finishDate).replace(/[\\/:*?"<>|]/g,'_') : new Date().toISOString().slice(0,10)
      const evalName = getEvaluationTypeName(metadata?.evaluationType, metadata?.blockName).replace(/[\\/:*?"<>|]/g,'_')

      BuildAndDownloadWorkbook(`Reporte_${studentForFile}_${evalName}_${when}.xlsx`, [
        { name: 'Resultados', ws }
      ])

      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte generado.' })
    } catch (error) {
      await LogErrors('ExportSingleReport - Results', error)
      showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ocurrió un error al generar el reporte.' })
    } finally {
      setIsLoading(false)
    }
  }

  // ===== Exportaciones generales (se mantiene) =====
  const ExportFilteredEvaluations = async () => {
    try {
      setIsLoading(true)

      if (!filteredRows?.length) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: 'No hay datos para exportar.' })
        return
      }

      showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte...' })

      const courseName     = (courses ?? []).find(c => c.id === selectedCourseId)?.name ?? '—'
      const groupName      = (groups ?? []).find(g => g.idGroup === selectedGroupId)?.groupName ?? '—'
      const evaluationName = (evaluations ?? []).find(e => e.idBlock === selectedEvaluationId)?.name ?? '—'

      const rows = filteredRows.map(r => ({
        fullName: `${r.name ?? ''} ${r.lastName ?? ''}`.trim(),
        user: r.user,
        attemptNumber: r.attemptNumber,
        blockName: r.blockName ?? r.nameEvaluation ?? r.type ?? '-',
        finishDate: r.finishDate ? FormatDateTime(r.finishDate) : '-',
        score: r.score
      }))

      const headerOrder = ['fullName', 'user', 'attemptNumber', 'blockName', 'finishDate', 'score']
      const headerLabels = {
        fullName: 'Nombre',
        user: 'Usuario',
        attemptNumber: 'Oportunidad',
        blockName: 'Tipo de evaluación',
        finishDate: 'Fecha y hora de finalización',
        score: 'Calificación'
      }
      const colWidths = [24, 16, 12, 38, 22, 14]

      const metaRows = [
        ['Curso:',        courseName],
        ['Grupo:',        groupName],
        ['Evaluación:',   evaluationName],
        ['Página actual:', pageNumber],
        ['Tamaño de página:', pageSize],
        ['Total registros (página):', rows.length]
      ]

      const ws = BuildSheetWithMeta({
        metaRows,
        rows,
        headerOrder,
        headerLabels,
        colWidths
      })

      BuildAndDownloadWorkbook(`Resultados_Desglosados_${new Date().toISOString().slice(0,10)}.xlsx`, [
        { name: 'Resultados', ws }
      ])

      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
    } catch (error) {
      await LogErrors('ExportFilteredEvaluations - Results', error)
      showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible exportar el reporte solicitado.' })
    } finally {
      setIsLoading(false)
    }
  }

  const ExportAllResults = async () => {
    try {
      setIsLoading(true)
      showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte...' })
      const response = await BLGetAllResults()

      if (response.success) {
        const rows = (response.data ?? []).map(e => ({
          groupName: e.groupName,
          courseName: e.courseName,
          name: e.name,
          lastName: e.lastName,
          email: e.email,
          user: e.user,
          blockName: e.blockName,
          attemptNumber: e.attemptNumber,
          finishDate: FormatDateTime(e.finishDate),
          score: e.score
        }))

        const headerOrder = ['groupName', 'courseName', 'name', 'lastName', 'email', 'user', 'blockName', 'attemptNumber', 'finishDate', 'score']
        const headerLabels = {
          groupName: 'Nombre del grupo',
          courseName: 'Curso',
          name: 'Nombre(s)',
          lastName: 'Apellidos',
          email: 'Correo',
          user: 'Usuario',
          blockName: 'Tipo de evaluación',
          attemptNumber: 'Oportunidad',
          finishDate: 'Fecha',
          score: 'Calificación'
        }

        const colWidths = [18, 16, 16, 16, 28, 16, 48, 12, 18, 14]
        const ws = JsonToSheet(rows, headerOrder, headerLabels, colWidths)

        BuildAndDownloadWorkbook(`Reporte_Completo_${new Date().toISOString().slice(0,10)}.xlsx`, [
          { name: 'Resultados', ws }
        ])

        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reporte listo para descargar.' })
      } else {
        showMessage({ type: MESSAGE_TYPE.BAD, text: 'No ha sido posible exportar el reporte solicitado.' })
      }
    } catch (error) {
      await LogErrors('ExportAllResults - Results', error)
      showMessage({ type: MESSAGE_TYPE.BAD, text: 'Ha ocurrido un error al exportar.' })
    } finally {
      setIsLoading(false)
    }
  }


  //useTawkChat(true) // Activamos el chat en esta página


  // ===== Render =====
  return (
    isLoading ? (
      <Loading />
    ) : (
      <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.INSTRUCTOR.RESULTS} page={WEBPAGES.INSTRUCTOR.RESULTS}>
        <section className='container Instructor__Reports'>
          <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Resultados de mis Ztudiantes</p>

          <button type='button' className='Btn__Ztudia--Primary m-auto mt-4' onClick={ExportAllResults}>
            Exportar reporte completo
          </button>

          <p className='Instructor__Text--Center Instructor__SpaceUp'>----- O -----</p>

          <>
            {/* Filtros encadenados */}
            <div className='Instructor__Results__Reports__Filters'>
              {/* CURSO */}
              {isCoursesListLoading ? (
                <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                  <option>Cargando lista...</option>
                </select>
              ) : (
                <select
                  className='Instructor__Results__Reports__Filters__List'
                  value={selectedCourseId}
                  onChange={(e) => handleSelectCourse(parseInt(e.target.value, 10))}
                  disabled={isWorking || !courses}
                >
                  <option value={0}>Selecciona un curso</option>
                  {(courses ?? []).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}

              {/* GRUPO */}
              {isGroupsListLoading ? (
                <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                  <option>Cargando lista...</option>
                </select>
              ) : (
                <select
                  className='Instructor__Results__Reports__Filters__List'
                  value={selectedGroupId}
                  onChange={(e) => handleSelectGroup(parseInt(e.target.value, 10))}
                  disabled={isWorking || !selectedCourseId || !groups}
                >
                  <option value={0}>Selecciona un grupo</option>
                  {(groups ?? []).map(g => (
                    <option key={g.idGroup} value={g.idGroup}>{g.groupName}</option>
                  ))}
                </select>
              )}

              {/* EVALUACIÓN */}
              {isEvaluationsListLoading ? (
                <select className='Instructor__Results__Reports__Filters__List' disabled={true}>
                  <option>Cargando lista...</option>
                </select>
              ) : (
                <select
                  className='Instructor__Results__Reports__Filters__List'
                  value={selectedEvaluationId}
                  onChange={(e) => handleSelectEvaluation(parseInt(e.target.value, 10))}
                  disabled={isWorking || !selectedGroupId || !evaluations}
                >
                  <option value={0}>Selecciona una evaluación</option>
                  {(evaluations ?? []).map(ev => (
                    <option key={ev.idBlock} value={ev.idBlock}>{ev.name}</option>
                  ))}
                </select>
              )}

              {/* APLICAR FILTRO */}
              <button
                type='button'
                className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__Filters__Button'
                onClick={() => GetFilteredResults(1, pageSize)}
                disabled={isWorking || !selectedCourseId || !selectedGroupId || !selectedEvaluationId}
              >
                <i className='bi bi-funnel'></i> Aplicar filtro
              </button>
            </div>

            {/* Buscador + Export (desglosado) */}
            <Search onChange={setSearchText} extraClassName={'Instructor__Reports__Ztudents__Search mt-5 mw-100'} />
            <button
              type='button'
              className='Btn__Ztudia--PrimaryOutline Instructor__Results__Reports__ExportButton'
              onClick={ExportFilteredEvaluations}
              disabled={!filteredRows?.length}
            >
              <i className='bi bi-filetype-xlsx'></i> Exportar
            </button>

            {/* Tabla de resultados DESGLOSADOS */}
            <section className='Tables__Container Instructor__Results__Reports__ContainerTable Tables--Shadow Tables--Background'>
              <table className='Tables__Table Instructor__Results__Reports__ContainerTable__Table'>
                <thead>
                  <tr className='Tables__Table__Row--Header'>
                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>Nombre</td>
                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>Usuario</td>
                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>Tipo de evaluación</td>
                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>Oportunidad</td>
                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>Fecha y hora de finalización</td>
                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>Calificación</td>
                    <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'></td>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows?.length ? (
                    filteredRows.map((r, idx) => (
                      <tr key={r.id ?? idx} className='Tables__Table__Row'>
                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>
                          {`${r.name ?? ''} ${r.lastName ?? ''}`.trim()}
                        </td>
                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>{r.user}</td>
                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>
                          {r.blockName ?? r.nameEvaluation ?? r.type ?? '-'}
                        </td>
                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>{r.attemptNumber}</td>
                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>
                          {r.finishDate ? `${FormatDateTime(r.finishDate)} hrs.` : '-'}
                        </td>
                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>{r.score ?? '-'}</td>
                        <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Results__Reports__ContainerTable__Table__TextColumn'>
                          <button
                            type='button'
                            className='Btn__Ztudia--PrimaryOutline'
                            onClick={() => ExportSingleReport(r)}
                          >
                            <i className='bi bi-filetype-xlsx'></i> Exportar
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className='Tables__Table__Row'>
                      <td className='Tables__Table__Row__Cell' colSpan={7}>
                        {isWorking ? 'Cargando...' : 'Sin resultados por mostrar.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            {/* Paginación */}
            <div className='Instructor__Reports__Pagination'>
              {/* <p className='Instructor__Reports__Pagination__Info text-center mt-3'>
                Página {pageNumber} | Cantidad de resultados a mostrar: {pageSize} {hasMore ? '· Hay más' : '· Fin'}
              </p> */}
              
              <p className='Instructor__Reports__Pagination__Info text-center mt-3'>Total de registros: {filteredRows.length}</p>

              <p className='Instructor__Reports__Pagination__Info text-center mt-3'>Página {pageNumber}</p>

              <div className='d-flex justify-content-center'>
                {pageNumber > 1 && (
                  <button
                    type='button'
                    className='Btn__Ztudia--PrimaryOutline me-2'
                    disabled={pageNumber <= 1 || isWorking}
                    onClick={gotoPrevPage}
                  >
                    <i className="bi bi-caret-left-fill"></i> Anterior
                  </button>
                )}

                {hasMore && (
                  <button
                    type='button'
                    className='Btn__Ztudia--PrimaryOutline ms-2'
                    disabled={!hasMore || isWorking}
                    onClick={gotoNextPage}
                  >
                    Siguiente <i className="bi bi-caret-right-fill"></i>
                  </button>
                )}
              </div>
            </div>
          </>

          {errorMessage && errorMessage !== '' && (<p className='Instructor__Message--Error'>{errorMessage}</p>)}
        </section>
      </InstructorBase>
    )
  )
}

export { Results }